import React from 'react';
import DSBox from '../../../../design-system/DSBox';
import { DSTypography } from '../../../../design-system/Typography';
import { ReactComponent as GlobalIcon } from '../../../../design-system/Icons/Global.svg';
import { ReactComponent as CollectionIcon } from '../../../../design-system/Icons/CollectionIcon.svg';
import { useTheme } from '@mui/material';
import {
  StatusKlydo,
  StatusRowKind,
  getKlydoStatusRows,
} from './klydoStatusRows';

type KlydoStatusProps = {
  klydo: StatusKlydo | undefined;
  /**
   * The design shortens the pending publication row to "Review" on gallery CARDS,
   * where space is tight; the klydo detail page spells it out. Every other row reads
   * the same in both places.
   */
  longPendingLabel?: boolean;
};

const ICONS: Record<StatusRowKind, React.ReactElement> = {
  public: <GlobalIcon />,
  collection: <CollectionIcon />,
};

/** Presentation only — the mapping lives in `klydoStatusRows`, where it is tested. */
const KlydoStatus: React.FC<KlydoStatusProps> = ({
  klydo,
  longPendingLabel,
}) => {
  const { spacing } = useTheme();
  const rows = getKlydoStatusRows(klydo, longPendingLabel);

  return (
    <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(0.5) }}>
      {rows.map((row) => (
        <DSBox
          key={row.text}
          sx={{ gap: spacing(1), display: 'flex', alignItems: 'center' }}
        >
          {React.cloneElement(
            ICONS[row.kind] as React.ReactElement<{
              style?: React.CSSProperties;
            }>,
            { style: { color: row.color, fontSize: spacing(2.5) } },
          )}
          <DSTypography
            variant="body1"
            color={row.color}
            fontSize={{ xs: 13.5, lg: 13, xl: 13.5 }}
          >
            {row.text}
          </DSTypography>
        </DSBox>
      ))}
    </DSBox>
  );
};

export default KlydoStatus;
